import classNames from 'classnames';

type ClassValue = string | undefined;

// Up to 5 levels of nesting, e.g. { dialog: { paper: { ... } } }
type Prev = [never, 0, 1, 2, 3];
type NestedClasses<Depth extends number = 4> = {
  [key: string]: ClassValue | (Depth extends 0 ? never : NestedClasses<Prev[Depth]>);
};

const isNestedClasses = (value: unknown): value is NestedClasses => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

export const mergeClasses = <T extends object, U extends object>(
  classesA: T = {} as T,
  classesB: U = {} as U
): T & U => {
  const classesRecordA = classesA as NestedClasses;
  const classesRecordB = classesB as NestedClasses;
  const keys = new Set([...Object.keys(classesRecordA), ...Object.keys(classesRecordB)]);
  const result: NestedClasses = {};

  keys.forEach((key) => {
    const valueA = classesRecordA[key];
    const valueB = classesRecordB[key];

    result[key] = isNestedClasses(valueA) || isNestedClasses(valueB)
      ? mergeClasses(
        isNestedClasses(valueA) ? valueA : {},
        isNestedClasses(valueB) ? valueB : {}
      )
      : classNames(valueA as ClassValue, valueB as ClassValue) || undefined;
  });

  return result as T & U;
};
